/* KD9 — /app/usuarios · Gestión de Usuarios (Director / Admin).
   Incluye recuperación de contraseña controlada por Dirección.
   Renderiza acciones según los permisos reales del usuario logueado. */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Users, Search, Crown, Trash2, Power, PowerOff, Check, X,
  ShieldCheck, AlertTriangle, Sparkles, User as UserIcon, RefreshCw, Copy,
  KeyRound, Eye, EyeOff, MessageCircle, BadgeCheck, Unlink,
} from "lucide-react";
import { PageShell } from "@/pages/dashboard/RolePages";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

const PROTECTED_EMAILS = new Set([
  "director@kd9.local",
  "admin@kd9.local",
  "soporte@kd9.local",
  "usuario@kd9.local",
  "consultor@kd9.local",
  "creador@kd9.local",
]);

const ROLE_LABEL = {
  user: "Usuario", student: "Estudiante", consultant: "Consultor",
  creator: "Creador", support: "Soporte", admin: "Administrador", director: "Director KD9",
};

const STATUS_META = {
  active:    { label: "Activa",     c: "#86EFAC", bg: "rgba(34,197,94,0.14)",  bd: "rgba(34,197,94,0.40)" },
  inactive:  { label: "Inactiva",   c: "#FCA5A5", bg: "rgba(122,14,23,0.10)",  bd: "rgba(122,14,23,0.40)" },
  suspended: { label: "Suspendida", c: "#FCD34D", bg: "rgba(212,175,55,0.16)", bd: "rgba(212,175,55,0.45)" },
};

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("es-AR", { year: "numeric", month: "short", day: "2-digit" }) : "—";

const GestionUsuarios = () => {
  const { accessMap, user: me } = useAuth();
  const perms = useMemo(() => new Set(accessMap?.permissions || []), [accessMap]);

  // Capabilities derived from real backend perms
  const canManageStatus  = perms.has("users.manage");
  const canDeleteUsers   = perms.has("users.delete");
  const canGrantPremium  = perms.has("premium.activate");
  const canRevokePremium = perms.has("premium.deactivate");
  const canView          = perms.has("users.view");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [confirmDelete, setConfirmDelete] = useState(null); // {id, email}
  const [passwordTarget, setPasswordTarget] = useState(null); // {id, email, name}
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [manualVerifyTarget, setManualVerifyTarget] = useState(null); // {id, email, name, phone}
  const [manualVerifyReason, setManualVerifyReason] = useState("");
  const [manualOtp, setManualOtp] = useState(null); // {id,email,name,phone,code,message,expires}
  const [releasePhoneTarget, setReleasePhoneTarget] = useState(null); // {id,email,name,phone,status}
  const [releasePhoneReason, setReleasePhoneReason] = useState("");

  const reload = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (roleFilter !== "all") params.set("role", roleFilter);
      params.set("limit", "200");
      const { data } = await api.get(`/api/admin/users?${params.toString()}`);
      setRows(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, roleFilter]);

  useEffect(() => { reload(); }, [reload]);

  const doAction = async (id, path, body, okMsg) => {
    setBusyId(id); setError(""); setOk("");
    try {
      if (body === "__DELETE__") {
        await api.delete(`/api/director/users/${id}`);
      } else {
        await api.post(`/api/director/users/${id}/${path}`, body || {});
      }
      setOk(okMsg);
      await reload();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusyId(null);
      setConfirmDelete(null);
    }
  };

  const activate   = (u) => doAction(u.id, "activate",   {}, `${u.email} activado.`);
  const deactivate = (u) => doAction(u.id, "deactivate", {}, `${u.email} desactivado.`);
  const suspend    = (u) => doAction(u.id, "suspend",    {}, `${u.email} suspendido.`);
  const grant   = (u, type) => doAction(u.id, "premium/grant",  { type }, `Premium ${type} activado para ${u.email}.`);
  const revoke  = (u, type) => doAction(u.id, "premium/revoke", { type }, `Premium ${type} quitado a ${u.email}.`);
  const remove  = (u) => doAction(u.id, null, "__DELETE__", `${u.email} eliminado.`);

  const openPasswordModal = (u) => {
    setPasswordTarget({ id: u.id, email: u.email, name: u.name || u.email });
    setNewPassword("");
    setConfirmPassword("");
    setShowPassword(false);
    setError("");
    setOk("");
  };

  const savePassword = async () => {
    if (!passwordTarget) return;
    if (newPassword.length < 8 || newPassword.length > 128) {
      setError("La contraseña debe tener entre 8 y 128 caracteres.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Las contraseñas no coinciden.");
      return;
    }
    setBusyId(passwordTarget.id);
    setError("");
    setOk("");
    try {
      const { data } = await api.post(`/api/director/users/${passwordTarget.id}/password`, {
        password: newPassword,
        confirm_password: confirmPassword,
      });
      setOk(`Contraseña establecida para ${passwordTarget.email}. ${data.revoked_sessions || 0} sesión(es) anterior(es) cerrada(s).`);
      setPasswordTarget(null);
      setNewPassword("");
      setConfirmPassword("");
      setShowPassword(false);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const sendWhatsAppVerificationCode = async (u) => {
    setBusyId(u.id); setError(""); setOk("");
    try {
      const { data } = await api.post(`/api/director/whatsapp/users/${u.id}/send-verification-code`);
      setOk(`Código de WhatsApp reenviado a ${data.phone_masked || u.phone_e164 || u.phone}. El flujo automático permanece activo.`);
      await reload();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const generateManualWhatsAppCode = async (u) => {
    setBusyId(u.id); setError(""); setOk("");
    try {
      const { data } = await api.post(`/api/director/whatsapp/users/${u.id}/manual-code`);
      setManualOtp({
        id: u.id,
        email: u.email,
        name: [u.name, u.surname].filter(Boolean).join(" ") || u.email,
        phone: data.phone || u.phone_e164 || u.phone || "",
        code: data.code,
        message: data.message,
        expires: data.expires_in_seconds,
      });
      setOk("Código manual generado. Envíalo desde la app WhatsApp Business; el usuario todavía debe escribirlo en KD9.");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const copyManualOtp = async () => {
    if (!manualOtp?.message) return;
    await navigator.clipboard.writeText(manualOtp.message);
    setOk("Mensaje con código copiado.");
  };

  const openManualOtpInWhatsApp = () => {
    if (!manualOtp?.phone || !manualOtp?.message) return;
    const digits = manualOtp.phone.replace(/\D/g, "");
    const url = `https://wa.me/${digits}?text=${encodeURIComponent(manualOtp.message)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const openManualVerify = (u) => {
    setManualVerifyTarget({
      id: u.id,
      email: u.email,
      name: [u.name, u.surname].filter(Boolean).join(" ") || u.email,
      phone: u.phone_e164 || u.phone || "",
    });
    setManualVerifyReason("Falla excepcional de entrega o visualización del código OTP.");
    setError("");
    setOk("");
  };

  const confirmManualVerify = async () => {
    if (!manualVerifyTarget || manualVerifyReason.trim().length < 5) return;
    setBusyId(manualVerifyTarget.id); setError(""); setOk("");
    try {
      const { data } = await api.post(
        `/api/director/whatsapp/users/${manualVerifyTarget.id}/manual-verify`,
        { reason: manualVerifyReason.trim() },
      );
      setOk(`WhatsApp de ${manualVerifyTarget.email} validado manualmente (${data.phone_masked || manualVerifyTarget.phone}). Acción auditada.`);
      setManualVerifyTarget(null);
      setManualVerifyReason("");
      await reload();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const openReleasePhone = (u) => {
    setReleasePhoneTarget({
      id: u.id,
      email: u.email,
      name: [u.name, u.surname].filter(Boolean).join(" ") || u.email,
      phone: u.phone_e164 || u.phone || "",
      status: u.status,
    });
    setReleasePhoneReason("Liberación administrativa autorizada del vínculo WhatsApp.");
    setError("");
    setOk("");
  };

  const confirmReleasePhone = async () => {
    if (!releasePhoneTarget || releasePhoneReason.trim().length < 10) return;
    setBusyId(releasePhoneTarget.id); setError(""); setOk("");
    try {
      const { data } = await api.post(
        `/api/director/users/${releasePhoneTarget.id}/whatsapp/release-number`,
        {
          phone: releasePhoneTarget.phone,
          reason: releasePhoneReason.trim(),
        },
      );
      setOk(`Vínculo WhatsApp ${data.phone_masked || releasePhoneTarget.phone} liberado. El número podrá verificarse en otra cuenta.`);
      setReleasePhoneTarget(null);
      setReleasePhoneReason("");
      await reload();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const isProtected = (u) => PROTECTED_EMAILS.has((u.email || "").toLowerCase());
  const isDirector  = (u) => (u.roles || []).includes("director");
  const isMe        = (u) => u.id === me?.id;

  const showActionCol = canManageStatus || canDeleteUsers || canGrantPremium || canRevokePremium;

  if (!canView) {
    return (
      <PageShell kicker="Sin acceso" title="GESTIÓN DE USUARIOS" desc="No tenés permiso para ver esta sección." testId="usuarios-page">
        <div className="kd9-card p-8 text-center font-mont text-sm" style={{ color: "#FCA5A5" }} data-testid="usuarios-forbidden">403 — Acceso denegado</div>
      </PageShell>
    );
  }

  return (
    <PageShell
      kicker={canDeleteUsers ? "Director KD9 · Gestión" : "Administrador · Consulta"}
      title="GESTIÓN DE USUARIOS"
      desc={canDeleteUsers ? "Control total: activar, desactivar, suspender, recuperar contraseñas, gestionar verificación y vínculos WhatsApp, eliminar y administrar Premium de Academia." : "Vista de consulta. Podés buscar y filtrar, pero no ejecutar acciones críticas."}
      testId="usuarios-page"
      badge={<span className="kd9-card px-4 py-2 text-[11px] font-mont tracking-widest uppercase" data-testid="usuarios-count">{rows.length} usuario{rows.length === 1 ? "" : "s"}</span>}
    >
      {/* Filters */}
      <div className="kd9-card p-4 mb-6 grid md:grid-cols-4 gap-3" data-testid="usuarios-filters">
        <div className="md:col-span-2">
          <div className="text-[10px] font-mont tracking-widest uppercase mb-1" style={{ color: "var(--kd9-text-soft)" }}>Buscar</div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--kd9-text-soft)" }} />
            <input
              className="kd9-input pl-9 pr-3 py-2 text-sm w-full"
              placeholder="ID KD9, nombre, correo o WhatsApp…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="usuarios-filter-search"
            />
          </div>
        </div>
        <div>
          <div className="text-[10px] font-mont tracking-widest uppercase mb-1" style={{ color: "var(--kd9-text-soft)" }}>Estado</div>
          <select className="kd9-input pl-3 py-2 text-sm w-full" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="usuarios-filter-status">
            <option value="all">Todos</option>
            <option value="active">Activa</option>
            <option value="inactive">Inactiva</option>
            <option value="suspended">Suspendida</option>
          </select>
        </div>
        <div>
          <div className="text-[10px] font-mont tracking-widest uppercase mb-1" style={{ color: "var(--kd9-text-soft)" }}>Rol</div>
          <select className="kd9-input pl-3 py-2 text-sm w-full" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} data-testid="usuarios-filter-role">
            <option value="all">Todos</option>
            <option value="user">Usuario</option>
            <option value="student">Estudiante</option>
            <option value="consultant">Consultor</option>
            <option value="creator">Creador</option>
            <option value="support">Soporte</option>
            <option value="admin">Administrador</option>
            <option value="director">Director KD9</option>
          </select>
        </div>
      </div>

      {error && <div className="mb-4 px-4 py-3 rounded-lg border text-sm font-mont" data-testid="usuarios-error"
        style={{ color: "#FCA5A5", borderColor: "rgba(122,14,23,0.4)", background: "rgba(122,14,23,0.10)" }}>{error}</div>}
      {ok && <div className="mb-4 px-4 py-3 rounded-lg border text-sm font-mont" data-testid="usuarios-ok"
        style={{ color: "#86EFAC", borderColor: "rgba(34,197,94,0.4)", background: "rgba(34,197,94,0.08)" }}>{ok}</div>}

      {loading ? (
        <div className="kd9-card p-8 text-center font-mont text-sm" style={{ color: "var(--kd9-text-soft)" }} data-testid="usuarios-loading">Cargando…</div>
      ) : rows.length === 0 ? (
        <div className="kd9-card p-8 text-center font-mont text-sm" style={{ color: "var(--kd9-text-soft)" }} data-testid="usuarios-empty">Ningún usuario coincide con los filtros.</div>
      ) : (
        <div className="kd9-card overflow-hidden" data-testid="usuarios-list">
          {rows.map((u, i) => {
            const status = STATUS_META[u.status] || STATUS_META.active;
            const flags = u.flags || {};
            const hasStudentPremium = !!(flags.academy_premium);
            const academyPremiumEligible = !(u.roles || []).some((role) =>
              ["consultant", "creator", "support", "admin", "director"].includes(role)
            );
            const protectedU = isProtected(u);
            const directorU = isDirector(u);
            const selfU = isMe(u);
            const disableDelete = protectedU || directorU || selfU;
            const rowBusy = busyId === u.id;

            return (
              <div key={u.id} className="border-b last:border-0 p-5 grid grid-cols-12 gap-4 items-center" style={{ borderColor: "var(--kd9-border)" }} data-testid={`usuario-row-${i}`}>
                {/* Identity */}
                <div className="col-span-12 md:col-span-3">
                  <div className="flex items-center gap-2">
                    <UserIcon size={14} className="kd9-gold-text shrink-0" />
                    <div>
                      <div className="font-mont font-semibold text-sm" data-testid={`usuario-name-${i}`}>{[u.name, u.surname].filter(Boolean).join(" ") || u.email}</div>
                      <div className="text-[11px] font-mont" style={{ color: "var(--kd9-text-soft)" }} data-testid={`usuario-email-${i}`}>{u.email}</div>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <code className="text-[10px] font-mont tracking-wide" style={{ color: "var(--kd9-accent)" }} data-testid={`usuario-kd9-id-${i}`}>
                          {u.kd9_id || "ID pendiente"}
                        </code>
                        {u.kd9_id && <button
                          type="button"
                          onClick={() => navigator.clipboard.writeText(u.kd9_id).then(() => setOk("ID KD9 copiado."))}
                          className="kd9-btn-outline min-h-8 rounded-lg px-2 text-[9px] inline-flex items-center gap-1"
                          aria-label={`Copiar ID KD9 de ${u.name || "usuario"}`}
                          data-testid={`usuario-copy-kd9-id-${i}`}
                        ><Copy size={10}/> Copiar</button>}
                      </div>
                      {(u.phone_e164 || u.phone) && (
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px] font-mont" data-testid={`usuario-whatsapp-status-${i}`}>
                          <span style={{ color: "var(--kd9-text-soft)" }}>{u.phone_e164 || u.phone}</span>
                          <span
                            className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5"
                            style={{
                              color: u.whatsapp_verified_at && !u.whatsapp_verification_required ? "#86EFAC" : "#FCD34D",
                              borderColor: u.whatsapp_verified_at && !u.whatsapp_verification_required ? "rgba(34,197,94,.4)" : "rgba(212,175,55,.45)",
                              background: u.whatsapp_verified_at && !u.whatsapp_verification_required ? "rgba(34,197,94,.08)" : "rgba(212,175,55,.08)",
                            }}
                          >
                            {u.whatsapp_verified_at && !u.whatsapp_verification_required ? <BadgeCheck size={10}/> : <MessageCircle size={10}/>}
                            {u.whatsapp_verified_at && !u.whatsapp_verification_required ? "WhatsApp verificado" : "WhatsApp pendiente"}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Roles + flags */}
                <div className="col-span-6 md:col-span-3 flex flex-wrap gap-1" data-testid={`usuario-roles-${i}`}>
                  {(u.roles || []).map((r) => (
                    <span key={r} className="px-2 py-0.5 rounded-full text-[9px] font-mont tracking-widest uppercase font-semibold"
                      style={{ background: r === "director" ? "rgba(212,175,55,0.14)" : "rgba(60,120,181,0.10)",
                               color: r === "director" ? "var(--kd9-gold)" : "#7BB8E8",
                               border: `1px solid ${r === "director" ? "rgba(212,175,55,0.35)" : "rgba(60,120,181,0.35)"}` }}
                      data-testid={`usuario-role-${r}-${i}`}>{ROLE_LABEL[r] || r}</span>
                  ))}
                  {hasStudentPremium && (
                    <span className="px-2 py-0.5 rounded-full text-[9px] font-mont tracking-widest uppercase font-semibold inline-flex items-center gap-1"
                      style={{ background: "rgba(212,175,55,0.14)", color: "var(--kd9-gold)", border: "1px solid rgba(212,175,55,0.45)" }}
                      data-testid={`usuario-flag-academy_premium-${i}`}>
                      <Crown size={9} /> Estudiante Premium
                    </span>
                  )}
                  {flags.creator_pending && (
                    <span className="px-2 py-0.5 rounded-full text-[9px] font-mont tracking-widest uppercase font-semibold"
                      style={{ background: "rgba(166,124,0,0.16)", color: "#C0A555", border: "1px solid rgba(166,124,0,0.45)" }}
                      data-testid={`usuario-flag-creator_pending-${i}`}>Creador pendiente</span>
                  )}
                </div>

                {/* Status */}
                <div className="col-span-6 md:col-span-2">
                  <span className="px-2.5 py-1 rounded-full text-[10px] font-mont tracking-widest uppercase font-semibold" data-testid={`usuario-status-${i}`}
                    style={{ background: status.bg, color: status.c, border: `1px solid ${status.bd}` }}>{status.label}</span>
                  <div className="text-[10px] font-mont mt-1" style={{ color: "var(--kd9-text-soft)" }} data-testid={`usuario-created-${i}`}>{fmtDate(u.created_at)}</div>
                </div>

                {/* Actions */}
                {showActionCol && (
                  <div className="col-span-12 md:col-span-4 flex flex-wrap gap-2 justify-start md:justify-end" data-testid={`usuario-actions-${i}`}>
                    {/* Status toggle — director only */}
                    {canManageStatus && !directorU && (
                      u.status === "active" ? (
                        <>
                          <button onClick={() => deactivate(u)} disabled={rowBusy}
                            className="kd9-btn-outline px-3 py-1.5 rounded-lg text-[10px] flex items-center gap-1"
                            data-testid={`usuario-deactivate-${i}`} title="Desactivar">
                            <PowerOff size={11} /> Desactivar
                          </button>
                          <button onClick={() => suspend(u)} disabled={rowBusy}
                            className="kd9-btn-outline px-3 py-1.5 rounded-lg text-[10px] flex items-center gap-1"
                            data-testid={`usuario-suspend-${i}`} title="Suspender acceso">
                            <AlertTriangle size={11} /> Suspender
                          </button>
                        </>
                      ) : (
                        <button onClick={() => activate(u)} disabled={rowBusy}
                          className="kd9-btn-outline px-3 py-1.5 rounded-lg text-[10px] flex items-center gap-1"
                          data-testid={`usuario-activate-${i}`} title="Activar">
                          <Power size={11} /> Activar
                        </button>
                      )
                    )}

                    {/* Premium grant/revoke — director only */}
                    {canGrantPremium && academyPremiumEligible && !hasStudentPremium && (
                      <button onClick={() => grant(u, "academy_premium")} disabled={rowBusy}
                        className="kd9-btn-outline px-3 py-1.5 rounded-lg text-[10px] flex items-center gap-1"
                        data-testid={`usuario-premium-grant-student-${i}`} title="Activar Estudiante Premium en Academia">
                        <Crown size={11} /> +Student
                      </button>
                    )}
                    {canRevokePremium && academyPremiumEligible && hasStudentPremium && (
                      <button onClick={() => revoke(u, "academy_premium")} disabled={rowBusy}
                        className="kd9-btn-outline px-3 py-1.5 rounded-lg text-[10px] flex items-center gap-1"
                        data-testid={`usuario-premium-revoke-student-${i}`} title="Desactivar Estudiante Premium en Academia">
                        <Crown size={11} /> −Student
                      </button>
                    )}

                    {/* Password recovery — director only. Never reveals stored credentials. */}
                    {canManageStatus && !directorU && (
                      <button onClick={() => openPasswordModal(u)} disabled={rowBusy}
                        className="kd9-btn-outline px-3 py-1.5 rounded-lg text-[10px] flex items-center gap-1"
                        data-testid={`usuario-password-${i}`} title="Establecer nueva contraseña">
                        <KeyRound size={11} /> Clave
                      </button>
                    )}

                    {/* WhatsApp recovery — Director fallback; automatic OTP stays unchanged */}
                    {canManageStatus && !directorU && (u.phone_e164 || u.phone) && u.whatsapp_opt_in && (!u.whatsapp_verified_at || u.whatsapp_verification_required || u.whatsapp_reverification_required) && (
                      <>
                        <button onClick={() => sendWhatsAppVerificationCode(u)} disabled={rowBusy}
                          className="kd9-btn-outline px-3 py-1.5 rounded-lg text-[10px] flex items-center gap-1"
                          data-testid={`usuario-whatsapp-send-code-${i}`} title="Reenviar código por el canal automático">
                          <MessageCircle size={11} /> Código auto
                        </button>
                        <button onClick={() => generateManualWhatsAppCode(u)} disabled={rowBusy}
                          className="kd9-btn-outline px-3 py-1.5 rounded-lg text-[10px] flex items-center gap-1"
                          data-testid={`usuario-whatsapp-manual-code-${i}`} title="Generar código para enviarlo manualmente desde WhatsApp Business">
                          <Copy size={11} /> Código manual
                        </button>
                        <button onClick={() => openManualVerify(u)} disabled={rowBusy}
                          className="kd9-btn-outline px-3 py-1.5 rounded-lg text-[10px] flex items-center gap-1"
                          data-testid={`usuario-whatsapp-manual-verify-${i}`} title="Validar manualmente como contingencia">
                          <BadgeCheck size={11} /> Validar WA
                        </button>
                      </>
                    )}

                    {/* WhatsApp ownership release — only for non-active accounts */}
                    {canManageStatus && !protectedU && !directorU && !selfU && (u.phone_e164 || u.phone) && u.status !== "active" && (
                      <button onClick={() => openReleasePhone(u)} disabled={rowBusy}
                        className="kd9-btn-outline px-3 py-1.5 rounded-lg text-[10px] flex items-center gap-1"
                        data-testid={`usuario-whatsapp-release-${i}`}
                        title="Liberar el número para que pueda verificarse en otra cuenta">
                        <Unlink size={11} /> Liberar WA
                      </button>
                    )}

                    {/* Delete — director only, guarded */}
                    {canDeleteUsers && !disableDelete && (
                      <button onClick={() => setConfirmDelete({ id: u.id, email: u.email })} disabled={rowBusy}
                        className="px-3 py-1.5 rounded-lg text-[10px] flex items-center gap-1 font-mont tracking-widest uppercase font-semibold border"
                        style={{ color: "#FCA5A5", borderColor: "rgba(122,14,23,0.5)", background: "rgba(122,14,23,0.06)" }}
                        data-testid={`usuario-delete-${i}`} title="Eliminar definitivamente">
                        <Trash2 size={11} /> Eliminar
                      </button>
                    )}

                    {canDeleteUsers && disableDelete && (
                      <span className="text-[10px] font-mont inline-flex items-center gap-1" style={{ color: "var(--kd9-text-soft)" }} data-testid={`usuario-delete-blocked-${i}`}>
                        <ShieldCheck size={11} /> {selfU ? "Vos" : directorU ? "Director" : "Seed protegido"}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Director password recovery modal */}
      {passwordTarget && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="usuario-password-modal"
          style={{ background: "rgba(0,0,0,0.72)" }}
          onClick={(e) => { if (e.target === e.currentTarget && busyId !== passwordTarget.id) setPasswordTarget(null); }}>
          <div className="kd9-card p-6 max-w-md w-full">
            <div className="flex items-center gap-2 mb-3">
              <KeyRound size={17} className="kd9-gold-text" />
              <div className="font-cinzel text-base tracking-widest kd9-gold-text">ESTABLECER CONTRASEÑA</div>
            </div>
            <p className="font-cormorant text-base mb-5">
              Nueva clave para <strong>{passwordTarget.name}</strong><br/>
              <span className="text-sm" style={{ color: "var(--kd9-text-soft)" }}>{passwordTarget.email}</span>
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-[10px] font-mont tracking-widest uppercase mb-1" style={{ color: "var(--kd9-text-soft)" }}>Nueva contraseña</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="kd9-input w-full px-3 py-2 pr-11 text-sm"
                    minLength={8}
                    maxLength={128}
                    autoComplete="new-password"
                    data-testid="usuario-password-new"
                  />
                  <button type="button" onClick={() => setShowPassword((value) => !value)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2"
                    aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}>
                    {showPassword ? <EyeOff size={15}/> : <Eye size={15}/>}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-mont tracking-widest uppercase mb-1" style={{ color: "var(--kd9-text-soft)" }}>Confirmar contraseña</label>
                <input
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="kd9-input w-full px-3 py-2 text-sm"
                  minLength={8}
                  maxLength={128}
                  autoComplete="new-password"
                  data-testid="usuario-password-confirm"
                />
              </div>
            </div>
            <div className="mt-4 rounded-lg border px-3 py-3 text-xs font-mont"
              style={{ color: "var(--kd9-text-soft)", borderColor: "var(--kd9-border)", background: "rgba(212,175,55,0.04)" }}>
              La clave se guarda cifrada con bcrypt. Al confirmar, KD9 cerrará las sesiones abiertas del usuario y la nueva contraseña será válida en el siguiente inicio de sesión.
            </div>
            <div className="flex gap-3 justify-end mt-6">
              <button onClick={() => setPasswordTarget(null)} disabled={busyId === passwordTarget.id}
                className="kd9-btn-outline px-4 py-2 rounded-lg text-[10px] flex items-center gap-2"
                data-testid="usuario-password-cancel">
                <X size={11} /> Cancelar
              </button>
              <button onClick={savePassword} disabled={busyId === passwordTarget.id || newPassword.length < 8 || confirmPassword.length < 8}
                className="kd9-btn-gold px-4 py-2 rounded-lg text-[10px] flex items-center gap-2"
                data-testid="usuario-password-save">
                <KeyRound size={11} /> {busyId === passwordTarget.id ? "Guardando…" : "Establecer clave"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Director WhatsApp ownership release modal */}
      {releasePhoneTarget && (
        <div className="fixed inset-0 z-[77] flex items-center justify-center p-4"
          data-testid="usuario-whatsapp-release-modal"
          style={{ background: "rgba(0,0,0,0.76)" }}
          onClick={(e) => { if (e.target === e.currentTarget && busyId !== releasePhoneTarget.id) setReleasePhoneTarget(null); }}>
          <div className="kd9-card p-6 max-w-md w-full">
            <div className="flex items-center gap-2 mb-3">
              <Unlink size={17} style={{ color: "#FCD34D" }} />
              <div className="font-cinzel text-base tracking-widest kd9-gold-text">LIBERAR WHATSAPP</div>
            </div>
            <p className="font-cormorant text-base">
              Liberar <strong>{releasePhoneTarget.phone}</strong> de <strong>{releasePhoneTarget.name}</strong>.
            </p>
            <p className="text-xs font-mont mt-1" style={{ color: "var(--kd9-text-soft)" }}>
              {releasePhoneTarget.email} · Cuenta {releasePhoneTarget.status}
            </p>
            <div className="mt-4 rounded-lg border px-3 py-3 text-xs font-mont"
              style={{ color: "#FCD34D", borderColor: "rgba(212,175,55,.4)", background: "rgba(212,175,55,.06)" }}>
              Esta acción elimina el número actual de esta cuenta y lo deja disponible para una nueva verificación. El historial del vínculo se conserva. Una cuenta activa no puede perder su número desde aquí.
            </div>
            <div className="mt-4">
              <label className="block text-[10px] font-mont tracking-widest uppercase mb-1" style={{ color: "var(--kd9-text-soft)" }}>
                Motivo obligatorio
              </label>
              <textarea
                value={releasePhoneReason}
                onChange={(e) => setReleasePhoneReason(e.target.value)}
                className="kd9-input w-full px-3 py-2 text-sm min-h-24"
                maxLength={500}
                data-testid="usuario-whatsapp-release-reason"
              />
            </div>
            <div className="flex gap-3 justify-end mt-6">
              <button onClick={() => setReleasePhoneTarget(null)} disabled={busyId === releasePhoneTarget.id}
                className="kd9-btn-outline px-4 py-2 rounded-lg text-[10px] flex items-center gap-2">
                <X size={11}/> Cancelar
              </button>
              <button onClick={confirmReleasePhone}
                disabled={busyId === releasePhoneTarget.id || releasePhoneReason.trim().length < 10}
                className="kd9-btn-gold px-4 py-2 rounded-lg text-[10px] flex items-center gap-2"
                data-testid="usuario-whatsapp-release-confirm">
                <Unlink size={11}/> {busyId === releasePhoneTarget.id ? "Liberando…" : "Confirmar liberación"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Director manual OTP delivery modal */}
      {manualOtp && (
        <div className="fixed inset-0 z-[76] flex items-center justify-center p-4" data-testid="usuario-whatsapp-manual-code-modal"
          style={{ background: "rgba(0,0,0,0.74)" }}
          onClick={(e) => { if (e.target === e.currentTarget) setManualOtp(null); }}>
          <div className="kd9-card p-6 max-w-md w-full">
            <div className="flex items-center gap-2 mb-3">
              <MessageCircle size={17} className="kd9-gold-text" />
              <div className="font-cinzel text-base tracking-widest kd9-gold-text">CÓDIGO MANUAL WHATSAPP</div>
            </div>
            <p className="font-cormorant text-base">
              Envío de contingencia para <strong>{manualOtp.name}</strong>
            </p>
            <p className="text-xs font-mont mt-1" style={{ color: "var(--kd9-text-soft)" }}>
              {manualOtp.email} · {manualOtp.phone}
            </p>

            <div className="mt-5 rounded-xl border p-4 text-center"
              style={{ borderColor: "rgba(212,175,55,.45)", background: "rgba(212,175,55,.07)" }}>
              <div className="text-[10px] font-mont tracking-widest uppercase mb-2" style={{ color: "var(--kd9-text-soft)" }}>
                Código vigente
              </div>
              <div className="font-cinzel text-3xl tracking-[0.3em] kd9-gold-text" data-testid="usuario-whatsapp-manual-code-value">
                {manualOtp.code}
              </div>
              <div className="text-[10px] font-mont mt-2" style={{ color: "var(--kd9-text-soft)" }}>
                Vence en {Math.round((manualOtp.expires || 300) / 60)} minutos
              </div>
            </div>

            <div className="mt-4 rounded-lg border px-3 py-3 text-xs font-mont"
              style={{ color: "#FCD34D", borderColor: "rgba(212,175,55,.4)", background: "rgba(212,175,55,.06)" }}>
              Este código no valida por sí solo. Debes enviarlo al número mostrado desde WhatsApp Business y el usuario debe escribirlo en KD9. El código no se guarda en texto plano.
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
              <button onClick={copyManualOtp}
                className="kd9-btn-outline px-4 py-2 rounded-lg text-[10px] flex items-center justify-center gap-2"
                data-testid="usuario-whatsapp-manual-code-copy">
                <Copy size={12}/> Copiar mensaje
              </button>
              <button onClick={openManualOtpInWhatsApp}
                className="kd9-btn-gold px-4 py-2 rounded-lg text-[10px] flex items-center justify-center gap-2"
                data-testid="usuario-whatsapp-manual-code-open">
                <MessageCircle size={12}/> Abrir WhatsApp
              </button>
            </div>
            <div className="flex justify-end mt-4">
              <button onClick={() => setManualOtp(null)}
                className="kd9-btn-outline px-4 py-2 rounded-lg text-[10px] flex items-center gap-2">
                <X size={11}/> Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Director manual WhatsApp verification modal */}
      {manualVerifyTarget && (
        <div className="fixed inset-0 z-[75] flex items-center justify-center p-4" data-testid="usuario-whatsapp-manual-modal"
          style={{ background: "rgba(0,0,0,0.72)" }}
          onClick={(e) => { if (e.target === e.currentTarget && busyId !== manualVerifyTarget.id) setManualVerifyTarget(null); }}>
          <div className="kd9-card p-6 max-w-md w-full">
            <div className="flex items-center gap-2 mb-3">
              <BadgeCheck size={17} className="kd9-gold-text" />
              <div className="font-cinzel text-base tracking-widest kd9-gold-text">VALIDACIÓN MANUAL WHATSAPP</div>
            </div>
            <p className="font-cormorant text-base">
              Contingencia para <strong>{manualVerifyTarget.name}</strong>
            </p>
            <p className="text-xs font-mont mt-1" style={{ color: "var(--kd9-text-soft)" }}>
              {manualVerifyTarget.email} · {manualVerifyTarget.phone}
            </p>
            <div className="mt-4 rounded-lg border px-3 py-3 text-xs font-mont"
              style={{ color: "#FCD34D", borderColor: "rgba(212,175,55,.4)", background: "rgba(212,175,55,.06)" }}>
              Usá esta opción solo cuando el usuario autorizó WhatsApp pero el código no puede visualizarse o entregarse. No modifica ni desactiva la verificación automática para otros usuarios.
            </div>
            <div className="mt-4">
              <label className="block text-[10px] font-mont tracking-widest uppercase mb-1" style={{ color: "var(--kd9-text-soft)" }}>
                Motivo de la excepción
              </label>
              <textarea
                value={manualVerifyReason}
                onChange={(e) => setManualVerifyReason(e.target.value)}
                className="kd9-input w-full px-3 py-2 text-sm min-h-24"
                maxLength={500}
                data-testid="usuario-whatsapp-manual-reason"
              />
            </div>
            <div className="flex gap-3 justify-end mt-6">
              <button onClick={() => setManualVerifyTarget(null)} disabled={busyId === manualVerifyTarget.id}
                className="kd9-btn-outline px-4 py-2 rounded-lg text-[10px] flex items-center gap-2">
                <X size={11} /> Cancelar
              </button>
              <button onClick={confirmManualVerify}
                disabled={busyId === manualVerifyTarget.id || manualVerifyReason.trim().length < 5}
                className="kd9-btn-gold px-4 py-2 rounded-lg text-[10px] flex items-center gap-2"
                data-testid="usuario-whatsapp-manual-confirm">
                <BadgeCheck size={11} /> {busyId === manualVerifyTarget.id ? "Validando…" : "Confirmar validación"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm delete modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="usuario-confirm-modal"
          style={{ background: "rgba(0,0,0,0.65)" }}
          onClick={(e) => { if (e.target === e.currentTarget) setConfirmDelete(null); }}>
          <div className="kd9-card p-6 max-w-md w-full">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={16} style={{ color: "#FCA5A5" }} />
              <div className="font-cinzel text-base tracking-widest" style={{ color: "#FCA5A5" }}>ELIMINAR DEFINITIVAMENTE</div>
            </div>
            <p className="font-cormorant text-base mb-6">
              Esta acción es irreversible. Se eliminará <strong>{confirmDelete.email}</strong> y todos sus role_requests.
              ¿Confirmás?
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirmDelete(null)} className="kd9-btn-outline px-4 py-2 rounded-lg text-[10px] flex items-center gap-2" data-testid="usuario-confirm-cancel">
                <X size={11} /> Cancelar
              </button>
              <button onClick={() => remove({ id: confirmDelete.id, email: confirmDelete.email })} disabled={busyId === confirmDelete.id}
                className="px-4 py-2 rounded-lg text-[10px] font-mont tracking-widest uppercase font-semibold border flex items-center gap-2"
                style={{ color: "#FCA5A5", borderColor: "rgba(122,14,23,0.5)", background: "rgba(122,14,23,0.10)" }}
                data-testid="usuario-confirm-delete">
                <Trash2 size={11} /> {busyId === confirmDelete.id ? "Eliminando…" : "Sí, eliminar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
};

export default GestionUsuarios;
